/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { useApolloClient } from '@apollo/client/react'
import { ConfirmDialog } from '@repo/shared-client/components/blocks'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Separator,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { Link, useNavigate } from '@tanstack/react-router'
import SFBackPageHeader from '@web/src/components/primatives/sf-back-page-header/sf-back-page-header'
import { useAuth } from '@web/src/hooks/use-auth'
import { useTranslation } from '@web/src/lib/intl/react'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import {
  ContactRoundIcon,
  CopyIcon,
  GiftIcon,
  LayoutDashboardIcon,
  ShieldIcon,
  UserCircleIcon,
  UserRoundSearchIcon,
} from 'lucide-react'
import { type ReactNode, useCallback, useState } from 'react'
import toast from 'react-hot-toast'
import SFProfileBanner from 'src/components/primatives/sf-profile-banner/sf-profile-banner'
import AccountEdit from '../admin-accounts-edit-dialog/account-edit/account-edit'
import BanningEdit from '../admin-accounts-edit-dialog/banning-edit/banning-edit'
import PointsEdit from '../admin-accounts-edit-dialog/points-edit/points-edit'
import ProfileEdit from '../admin-accounts-edit-dialog/profile-edit/profile-edit'
import { AdminProfilePayments, AdminProfileSupportStat } from '../admin-payments/admin-profile-payments'

dayjs.extend(relativeTime)
dayjs.extend(advancedFormat)

import { gql, type TypedDocumentNode } from '@apollo/client'
import { ProfileInfo, ProfileMediaInfo } from 'src/lib/fragments'
import type { AdminAccountEditQuery, AdminAccountEditQueryVariables } from 'types/graphql'

export const ADMIN_ACCOUNT_EDIT_QUERY: TypedDocumentNode<AdminAccountEditQuery, AdminAccountEditQueryVariables> = gql`
  query AdminAccountEditQuery($id: String!) {
    adminUser(id: $id) {
      id
      name
      email
      createdAt
      roles
      emailVerified
      roleExpiresAt
      bannedUntil
      banned
      theme
      seasonalThemesEnabled
      betaProgram
      moderationWarningCount
      moderationLastWarnedAt
      profile {
        ...ProfileInfo
        deletedAt
      }
    }
  }
  ${ProfileInfo}
  ${ProfileMediaInfo}
`

export type AdminAccountEditUser = NonNullable<AdminAccountEditQuery['adminUser']>

type AdminAccountEditPageProps = {
  user: AdminAccountEditUser
}

function OverviewStat({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <Card className={cn('overflow-hidden shadow-sm', className)}>
      <CardHeader className="space-y-1 pb-2">
        <CardDescription className="text-xs uppercase tracking-wide">{label}</CardDescription>
        <CardTitle className="font-medium text-base leading-snug">{children}</CardTitle>
      </CardHeader>
    </Card>
  )
}

export function AdminAccountEditPage(props: AdminAccountEditPageProps) {
  const { user } = props
  const { t } = useTranslation()
  const navigate = useNavigate()
  const client = useApolloClient()
  const { currentUser, impersonateUser, isImpersonating } = useAuth()
  const [impersonateModal, setImpersonateModal] = useState(false)
  const [impersonating, setImpersonating] = useState(false)

  const handleBackToAccounts = useCallback(() => {
    navigate({ to: '/admin/accounts' })
  }, [navigate])

  const refreshAdminUser = useCallback(async () => {
    await client.refetchQueries({
      include: [ADMIN_ACCOUNT_EDIT_QUERY],
    })
  }, [client])

  const copyUserId = () => {
    navigator.clipboard.writeText(user.id).then(() => {
      toast.success(t('User id copied to clipboard'))
    })
  }

  const canImpersonate = Boolean(currentUser?.id && currentUser.id !== user.id && !isImpersonating)

  const handleImpersonate = async () => {
    setImpersonateModal(false)
    setImpersonating(true)
    try {
      await impersonateUser.mutateAsync({ userId: user.id })
      toast.success(t('Now impersonating {{name}}', { name: user.profile?.name || user.name || user.email }))
      window.location.assign('/')
    } catch (error) {
      console.error(error)
      toast.error(error instanceof Error ? error.message : t('Failed to impersonate user'))
      setImpersonating(false)
    }
  }

  const joined = user.createdAt
    ? {
        absolute: dayjs(user.createdAt).format('MMMM D, YYYY'),
        relative: dayjs(user.createdAt).fromNow(),
      }
    : null

  const roleExpires = user.roleExpiresAt
    ? {
        absolute: dayjs(user.roleExpiresAt).format('MMMM D, YYYY'),
        relative: dayjs(user.roleExpiresAt).fromNow(),
      }
    : null

  const banUntil = user.bannedUntil
    ? {
        absolute: dayjs(user.bannedUntil).format('MMMM D, YYYY'),
        relative: dayjs(user.bannedUntil).fromNow(),
      }
    : null

  const isPermanentBan = Boolean(user.banned && user.bannedUntil === null)
  const profile = user.profile
  const lastWarned = user.moderationLastWarnedAt
    ? {
        absolute: dayjs(user.moderationLastWarnedAt).format('MMMM D, YYYY'),
        relative: dayjs(user.moderationLastWarnedAt).fromNow(),
      }
    : null

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-3 pt-4 pb-12 md:px-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <SFBackPageHeader
            lowerText={
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  {user.banned ? <Badge variant="secondary">{t('Banned')}</Badge> : null}
                  {user.emailVerified ? null : <Badge variant="destructive">{t('Email Unverified')}</Badge>}
                  {profile?.deletedAt ? <Badge variant="outline">{t('Profile deleted')}</Badge> : null}
                </div>
              </div>
            }
            onBackClick={handleBackToAccounts}
            title={t('Edit account')}
          />
        </div>
        {canImpersonate ? (
          <div className="flex shrink-0 items-center md:pt-1">
            <Button disabled={impersonating} onClick={() => setImpersonateModal(true)} type="button" variant="outline">
              {impersonating ? <Spinner className="size-4" /> : <UserRoundSearchIcon className="size-4" />}
              {t('Impersonate')}
            </Button>
          </div>
        ) : null}
      </div>

      <Card className="overflow-hidden border-border/80 py-0 shadow-sm">
        <CardContent className="p-4 md:p-5">
          <SFProfileBanner
            lowerTitle={
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-muted-foreground text-sm">{user.email}</span>
                <Badge variant={user.emailVerified ? 'outline' : 'destructive'}>
                  {user.emailVerified ? t('Email Verified') : t('Email Unverified')}
                </Badge>
              </div>
            }
            profile={profile ?? undefined}
          />
        </CardContent>
      </Card>

      <Tabs className="w-full gap-6" defaultValue="overview">
        <TabsList className="w-full justify-start overflow-x-auto overflow-y-hidden" variant="tabs">
          <TabsTrigger className="gap-1.5" value="overview">
            <LayoutDashboardIcon className="size-4" />
            {t('Overview')}
          </TabsTrigger>
          <TabsTrigger className="gap-1.5" value="account">
            <UserCircleIcon className="size-4" />
            {t('Account & preferences')}
          </TabsTrigger>
          <TabsTrigger className="gap-1.5" value="moderation">
            <ShieldIcon className="size-4" />
            {t('Moderation')}
          </TabsTrigger>
          <TabsTrigger className="gap-1.5" value="profile">
            <ContactRoundIcon className="size-4" />
            {t('Profile')}
          </TabsTrigger>
          <TabsTrigger className="gap-1.5" value="points">
            <GiftIcon className="size-4" />
            {t('Points')}
          </TabsTrigger>
        </TabsList>

        <TabsContent className="outline-none" value="overview">
          <div className="flex flex-col gap-6">
            <div className="grid gap-3 md:grid-cols-3">
              <OverviewStat label={t('User id')}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="break-all font-mono text-muted-foreground text-xs">{user.id}</span>
                  <Button
                    aria-label={t('CopyIcon user id')}
                    className="shrink-0"
                    onClick={copyUserId}
                    size="icon-sm"
                    type="button"
                    variant="outline"
                  >
                    <CopyIcon className="size-4" />
                  </Button>
                </div>
              </OverviewStat>
              <OverviewStat label={t('Joined')}>
                {joined ? (
                  <span>
                    {joined.absolute}
                    <span className="mt-1 block font-normal text-muted-foreground text-sm">{joined.relative}</span>
                  </span>
                ) : (
                  '—'
                )}
              </OverviewStat>
              <OverviewStat label={t('Membership')}>
                <span className="capitalize">{user.roles ?? '—'}</span>
              </OverviewStat>
              <OverviewStat label={t('Moderation warnings')}>
                <span>{user.moderationWarningCount ?? 0}</span>
                {lastWarned ? (
                  <span className="mt-1 block font-normal text-muted-foreground text-sm">
                    {t('Last warned {{when}}', { when: lastWarned.relative })}
                  </span>
                ) : null}
                {profile?.id ? (
                  <Link
                    className="mt-2 block font-normal text-sm underline"
                    search={{ subjectProfileId: profile.id } as never}
                    to="/admin/reports"
                  >
                    {t('Open reports for this profile')}
                  </Link>
                ) : null}
              </OverviewStat>
              {profile?.id ? <AdminProfileSupportStat profileId={profile.id} /> : null}
            </div>

            <Separator />

            <div className="grid gap-3 md:grid-cols-2">
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle className="text-base">{t('Access & billing')}</CardTitle>
                  <CardDescription>{t('Role expiry and verification at a glance.')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-muted-foreground">{t('Role expires')}</span>
                    {roleExpires ? (
                      <span>
                        {roleExpires.absolute} <span className="text-muted-foreground">({roleExpires.relative})</span>
                      </span>
                    ) : (
                      <span>—</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-muted-foreground">{t('Theme')}</span>
                    <span className="capitalize">{user.theme ?? '—'}</span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-muted-foreground">{t('Beta Program')}</span>
                    <span>{user.betaProgram ? t('Enabled') : t('Disabled')}</span>
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle className="text-base">{t('Moderation summary')}</CardTitle>
                  <CardDescription>
                    {t('Ban state shown for quick reference; use the Moderation tab to change it.')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-muted-foreground">{t('Banned until')}</span>
                    {isPermanentBan ? (
                      <span className="font-medium text-destructive">{t('Permanent ban')}</span>
                    ) : banUntil ? (
                      <span>
                        {banUntil.absolute} <span className="text-muted-foreground">({banUntil.relative})</span>
                      </span>
                    ) : (
                      <span>—</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-muted-foreground">{t('Display name (account)')}</span>
                    <span>{user.name?.trim() ? user.name : '—'}</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {profile?.id ? <AdminProfilePayments profileId={profile.id} /> : null}
          </div>
        </TabsContent>

        <TabsContent className="outline-none" value="account">
          <AccountEdit onAccountUpdated={refreshAdminUser} user={user} />
        </TabsContent>

        <TabsContent className="outline-none" value="moderation">
          <BanningEdit onModerationUpdated={refreshAdminUser} user={user} />
        </TabsContent>

        <TabsContent className="outline-none" value="profile">
          {profile?.id ? (
            <ProfileEdit onProfileUpdated={refreshAdminUser} user={{ ...user, profile }} />
          ) : (
            <Card className="border-dashed">
              <CardHeader>
                <CardTitle className="text-base">{t('No profile')}</CardTitle>
                <CardDescription>{t('This user record has no linked profile yet.')}</CardDescription>
              </CardHeader>
            </Card>
          )}
        </TabsContent>

        <TabsContent className="outline-none" value="points">
          <PointsEdit onPointsUpdated={refreshAdminUser} user={user} />
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        confirmText={t('Impersonate')}
        onConfirm={handleImpersonate}
        onOpenChange={(e) => setImpersonateModal(e.open)}
        open={impersonateModal}
        text={t(
          'You will browse the site as this user. Your admin session is preserved and you can exit impersonation at any time.'
        )}
        title={t('Impersonate this user?')}
        variant="basic"
      />
    </div>
  )
}
